import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/components/ProfileCompletePrompt.js (actual prompt fields)
//   - src/pages/Settings.js (tab names: Profile, Login & security, Email
//     template, Auto-reminders, Team, Billing; notify_on_completion toggle
//     lives on the Email template tab, NOT Profile)
//   - src/pages/Dashboard.js (no notifications system; client list + search
//     + two summary stat cards at the top)
//   - src/pages/SignUp.js (30-day trial confirmed)
//   - src/lib/supabase.js (notify_on_completion setting drives email
//     notification when a client finishes a request)
// Removed:
//   - Old article claimed prompt asks for "practice name, brand colour, logo"
//   - Old article claimed Settings to Practice (no such tab)
//   - Old article claimed dashboard shows notifications (it doesn't)
//   - Old article promised 90-day post-cancellation data retention. The
//     real policy is 7 years (per Terms section 9), but retention isn't
//     in scope for a getting-started article -- it belongs in the
//     changing-or-cancelling-your-plan article instead.
//   - "We will email you well before that deadline" (no such automated email)
// Pass 2 corrections:
//   - Notify-on-completion toggle is on Settings -> Email template, not Profile
//   - Dashboard description now mentions the two stat cards at the top

export default {
  title:   'Setting up your account',
  summary: 'What happens after you sign up, and the first things to configure before sending requests.',
  body: (
    <>
      <P>
        When you sign up, Receipt Chaser creates two things at once: a
        practice (your firm) and a user account (you, as the practice
        owner). Your 30-day free trial starts immediately, with full access
        to every feature on every plan. No card required.
      </P>

      <H>The first time you sign in</H>
      <P>
        Right after signing up you'll see a welcome prompt asking for your{' '}
        <strong>practice name</strong> and <strong>your full name</strong>.
        Both fields are required if you fill the prompt in. You can also
        click <Code>Skip for now</Code> and come back to it later.
      </P>

      <Screenshot
        src="/support-screenshots/01 - sign-up info.png"
        alt="The welcome modal asking for practice name and your full name, with Skip for now and Save and continue buttons."
        caption="The welcome prompt shown on first sign-in, asking for practice name and your full name."
      />

      <P>
        These two fields appear in the emails your clients receive, so
        filling them in early means your first client sees your real
        practice name rather than the fallback "Your bookkeeper". You can
        update them any time from <Code>Settings → Profile</Code>.
      </P>

      <H>What's on the dashboard</H>
      <P>
        The dashboard is your starting point every time you sign in. It
        shows:
      </P>
      <UL>
        <Li>Two summary cards: a count of your active clients, and the total outstanding receipts across all of them</Li>
        <Li>A list of your active clients with their outstanding-request count beside each</Li>
        <Li>A search box for finding a client by name, email, contact name, or internal reference</Li>
        <Li>A button to add a new client</Li>
        <Li>A section for archived clients (hidden by default; toggle it open from the link below the active list)</Li>
      </UL>

      <Screenshot
        src="/support-screenshots/01 - dashboard.png"
        alt="The Your Clients dashboard showing two summary cards (Active clients and Outstanding receipts), a search box, and a list of clients with outstanding-count badges and action buttons."
        caption="The dashboard view, showing the two summary cards, the client list with outstanding counts, and the search box."
      />

      <P>
        Sending a new request happens from the <strong>client detail
        page</strong>, not the dashboard. Click a client to open it, then
        click <Code>+ New receipt request</Code>.
      </P>

      <H>Notifications when a client completes a request</H>
      <P>
        When a client finishes uploading every receipt (or marks the
        remaining transactions as lost), Receipt Chaser sends you an
        email saying the request is complete. This is on by default and
        can be turned off from{' '}
        <Code>Settings → Email template</Code> under the "Notify me when
        a client completes a request" toggle.
      </P>
      <P>
        The dashboard itself doesn't show notifications. The outstanding
        count next to each client is the at-a-glance signal that there's
        nothing more to chase.
      </P>

      <H>Before you send your first request</H>
      <P>
        Three things are worth setting up upfront, in this order:
      </P>
      <OL>
        <Li>
          <strong>Your profile details.</strong> Practice name and your
          full name in <Code>Settings → Profile</Code>. These appear in
          every email your clients receive.
        </Li>
        <Li>
          <strong>Your team, if you have one.</strong> Adding practice
          users is included on every plan at no extra cost. See{' '}
          <Link to="/support/managing-practice-users">Managing practice users</Link>.
        </Li>
        <Li>
          <strong>At least one client.</strong> A client is the entity
          you're chasing receipts for. See{' '}
          <Link to="/support/adding-your-first-client">Adding your first client</Link>.
        </Li>
      </OL>

      <Callout variant="tip" title="Try it on yourself first">
        Before sending a real request to a real client, add yourself as a
        test client using a personal email address and send yourself a
        request. It takes two minutes and lets you see exactly what your
        clients will see.
      </Callout>
    </>
  ),
}
